/**
 * ONE global "who is allowed to make sound right now" bus.
 *
 * Why this exists: members reported that starting a second meditation while a
 * first one was still playing produced two overlapping voices. Every screen
 * used to own its own playback, so nothing told the previous player to stop.
 *
 * How it works:
 *  - Every <audio>/<video> element goes through a patched `play()`. Starting
 *    one pauses every other media element the page has started and stops any
 *    registered non-element player (Web Audio soundscapes, TTS sequences).
 *  - Screens that play through Web Audio, or that run a multi-segment voice
 *    sequence, register an owner with a `stop()` that also resets their UI.
 *
 * Rules for callers:
 *  - Element playback: nothing to do, the patch handles it. Just make sure the
 *    component listens to the element's `pause` event so its button updates.
 *  - Web Audio / sequence playback: `registerAudioOwner({ stop, ownsElement })`
 *    on mount, call the returned dispose on unmount, and call `claimAudio(me)`
 *    right before you start making sound.
 */

export type AudioOwner = {
  /** Stop this player and reset its visible play/pause state. */
  stop: () => void;
  /** True when the given element belongs to this owner (so it isn't paused). */
  ownsElement?: (el: HTMLMediaElement) => boolean;
  /**
   * The global mini-player keeps playing across route changes, so navigation
   * stops must skip it. It is still stopped when another player claims audio.
   */
  persistent?: boolean;
};

const owners = new Set<AudioOwner>();
const startedElements = new Set<HTMLMediaElement>();
let installed = false;

/** Register a player that is not a plain media element. Returns a disposer. */
export function registerAudioOwner(owner: AudioOwner): () => void {
  owners.add(owner);
  return () => {
    owners.delete(owner);
  };
}

function stopOtherOwners(
  self: AudioOwner | null,
  el: HTMLMediaElement | null,
  opts: { keepPersistent?: boolean } = {},
) {
  for (const owner of Array.from(owners)) {
    if (owner === self) continue;
    if (opts.keepPersistent && owner.persistent) continue;
    if (el && owner.ownsElement?.(el)) continue;
    try {
      owner.stop();
    } catch {
      /* a broken player must never block the new one */
    }
  }
}

function pauseOtherElements(
  keep: HTMLMediaElement | null,
  self: AudioOwner | null,
  opts: { keepPersistent?: boolean } = {},
) {
  const candidates = new Set<HTMLMediaElement>(startedElements);
  if (typeof document !== "undefined") {
    document.querySelectorAll<HTMLMediaElement>("audio, video").forEach((el) => candidates.add(el));
  }
  for (const el of candidates) {
    if (el === keep) continue;
    if (el.paused) continue;
    if (el.dataset?.audioBusIgnore === "true") continue;
    if (self?.ownsElement?.(el)) continue;
    if (opts.keepPersistent && Array.from(owners).some((o) => o.persistent && o.ownsElement?.(el))) continue;
    try {
      el.pause();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Take the single audio slot. Everything else currently making sound stops.
 * Call this synchronously inside the tap handler that starts playback.
 */
export function claimAudio(self?: AudioOwner | null): void {
  stopOtherOwners(self ?? null, null);
  pauseOtherElements(null, self ?? null);
}

/**
 * Stop everything (emergency exit, hard resets).
 * Pass `{ keepPersistent: true }` on route changes so the global mini-player,
 * which is deliberately designed to follow the member between pages, survives.
 */
export function stopAllAudio(opts: { keepPersistent?: boolean } = {}): void {
  stopOtherOwners(null, null, opts);
  pauseOtherElements(null, null, opts);
}

/** Patch media playback once, as early as possible on the client. */
export function installAudioBus(): void {
  if (installed || typeof window === "undefined" || typeof HTMLMediaElement === "undefined") return;
  installed = true;

  const originalPlay = HTMLMediaElement.prototype.play;
  HTMLMediaElement.prototype.play = function patchedPlay(this: HTMLMediaElement) {
    // Silent keep-alive elements and muted background video never claim audio.
    const ignored = this.dataset?.audioBusIgnore === "true" || this.muted;
    if (!ignored) {
      const mine = Array.from(owners).find((o) => o.ownsElement?.(this)) ?? null;
      stopOtherOwners(mine, this);
      pauseOtherElements(this, mine);
      startedElements.add(this);
      const forget = () => {
        startedElements.delete(this);
        this.removeEventListener("emptied", forget);
      };
      this.addEventListener("emptied", forget);
    }
    return originalPlay.call(this);
  };
}
